/**
 * Fetches a CSV in parallel chunks via a worker pool and returns a single
 * concatenated Blob. The first request reads `X-Total-Count` to discover
 * how many chunks to fan out; chunks beyond the first omit headers.
 *
 * @throws {Error} If the first response lacks a valid X-Total-Count header,
 *   or any chunk fails.
 */
export default async function downloadChunkedCsv({
  requestChunk,
  chunkSize = 500,
  concurrency = 6,
  onProgress
}) {
  let firstResponse;
  try {
    firstResponse = await requestChunk({
      offset: 0,
      limit: chunkSize,
      skipHeaders: false
    });
  } catch (err) {
    throw await translateAxiosBlobError(err);
  }

  const totalCountHeader = firstResponse.headers["x-total-count"];
  const totalCount = parseInt(totalCountHeader, 10);
  if (!Number.isFinite(totalCount)) {
    throw new Error(
      `Missing or invalid X-Total-Count header (got "${totalCountHeader}") — ` +
        `cannot determine total chunks, refusing to silently truncate.`
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / chunkSize));
  const chunks = new Array(totalPages);
  chunks[0] = firstResponse.data;

  let loaded = 1;
  onProgress?.({ loaded, total: totalPages });

  if (totalPages > 1) {
    let nextPage = 1;
    let aborted = false;

    const worker = async () => {
      while (!aborted) {
        const index = nextPage++;
        if (index >= totalPages) return;
        let response;
        try {
          response = await requestChunk({
            offset: index * chunkSize,
            limit: chunkSize,
            skipHeaders: true
          });
        } catch (err) {
          aborted = true;
          throw await translateAxiosBlobError(err);
        }
        if (aborted) return;
        chunks[index] = response.data;
        loaded++;
        onProgress?.({ loaded, total: totalPages });
      }
    };

    const workerCount = Math.min(concurrency, totalPages - 1);
    await Promise.all(Array.from({ length: workerCount }, worker));
  }

  // Catch missing slots before they become "undefined" string in the CSV.
  if (chunks.some((c) => c === undefined)) {
    throw new Error("Chunked CSV download produced an incomplete result.");
  }

  return new Blob(chunks, { type: "text/csv" });
}

// With `responseType: "blob"`, axios puts the error body in a Blob, which
// `toastError` would render as "[object Blob]". Read + parse so the user
// sees the backend's actual error message.
async function translateAxiosBlobError(err) {
  const data = err?.response?.data;
  if (!(data instanceof Blob)) return err;
  const text = await data.text().catch(() => null);
  if (!text) return err;
  let parsed = null;
  try {
    parsed = JSON.parse(text);
  } catch {
    /* not JSON — fall through to raw text */
  }
  const message =
    parsed?.error?.message ||
    parsed?.message ||
    (Array.isArray(parsed?.messages) ? parsed.messages.join("\n") : null) ||
    text;
  const translated = new Error(message);
  translated.response = err.response;
  return translated;
}
